import React, {useState, useEffect, useRef} from 'react';
import {text} from '../text';
import {hooks} from '../hooks';
import {components} from '../components';
import {theme} from '../constants';
import {svg} from '../assets/svg';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import { compressImage } from '../utils/imageUtils';
import { MdCheckCircle, MdError, MdWarning, MdAttachFile, MdDelete } from 'react-icons/md';

const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const AttachFileIcon = MdAttachFile as any;
const DeleteIcon = MdDelete as any;

export const LeaveRequest: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();
  
  const [leaveRemaining, setLeaveRemaining] = useState<number | null>(null);
  const [leaveType, setLeaveType] = useState('Paid Time Off (PTO)');
  const [reason, setReason] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);

  const minStartDate = React.useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    axiosInstance.get('/employees/profile').then(res => {
      const remaining = res.data?.leave_remaining ?? null;
      setLeaveRemaining(remaining);
      if (remaining === 0) setLeaveType('Absent');
    }).catch(() => {});
  }, []);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({
      isOpen: true,
      title,
      message,
      type,
      onClose,
    });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) {
      onClose();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0];
      if (file.type.startsWith('image/')) {
        try {
          file = await compressImage(file);
        } catch (error) {
          console.error("Compression error:", error);
        }
      }
      setAttachment(file);
    }
  };

  const handleSubmit = async () => {
    if (!leaveType || !startDate || !endDate || !reason) {
      showAlert(t.common.warning, t.leaveRequest.validation, 'warning');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      showAlert(t.common.warning, t.common.error, 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('leave_type', leaveType);
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('reason', reason);
      if (attachment) {
        formData.append('attachment', attachment);
      }

      await axiosInstance.post('/leaves', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showAlert(t.common.success, t.leaveRequest.success, 'success', () => {
        navigate(-1);
      });
    } catch (error) {
      console.error('Failed to submit leave request:', error);
      showAlert(t.common.error, t.leaveRequest.error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => {
    return <components.Header title={t.leaveRequest.title} goBack={true} />;
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}
        >
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {alertConfig.message}
          </text.T16>
          <components.Button title={t.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <main className='container' style={{marginTop: 60, paddingBottom: 20}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <text.H5>{t.leaveRequest.leaveType}</text.H5>
          {leaveRemaining !== null && (
            <text.T12 style={{
              color: leaveRemaining === 0 ? '#EF4444' : '#6B7280',
              fontWeight: '600',
            }}>
              {(t.leaveRequest as any).remaining || 'Sisa'}: {leaveRemaining} {(t.leaveRequest as any).days || 'hari'}
            </text.T12>
          )}
        </div>
        <div style={{marginBottom: 20}}>
            <select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                style={{
                width: '100%', padding: 14, borderRadius: 10,
                border: '1px solid #FFEFE6', backgroundColor: '#fff',
                fontSize: 16, fontFamily: 'SourceSansPro_400Regular'
            }}>
                <option value="Paid Time Off (PTO)" disabled={leaveRemaining === 0}>
                  Paid Time Off (PTO){leaveRemaining === 0 ? ` (${(t.leaveRequest as any).noBalance || 'sisa 0'})` : ''}
                </option>
                <option value="Sick Leave">{t.leaveRequest.sickLeave}</option>
                <option value="Absent">{t.leaveRequest.unpaidLeave}</option>
                <option value="Maternity Leave">{t.leaveRequest.maternityLeave}</option>
                <option value="Marriage Leave">{t.leaveRequest.marriageLeave}</option>
            </select>
        </div>

        <text.H5 style={{marginBottom: 10}}>{t.leaveRequest.dates}</text.H5>
        <div style={{display: 'flex', gap: 10, marginBottom: 20}}>
            <div style={{
              flex: 1, minWidth: 0, height: 50,
              paddingLeft: 10, paddingRight: 10,
              border: '1px solid #FFEFE6', borderRadius: 10,
              backgroundColor: '#fff', display: 'flex', alignItems: 'center',
            }}>
              <input
                type="date"
                value={startDate}
                min={minStartDate}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setStartDate(newStart);
                  if (!endDate || endDate < newStart) setEndDate(newStart);
                }}
                style={{
                  width: '100%', height: '100%', border: 'none', outline: 'none',
                  backgroundColor: 'transparent', fontSize: 16,
                  color: startDate ? theme.colors.mainDark : '#9CA3AF',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <div style={{
              flex: 1, minWidth: 0, height: 50,
              paddingLeft: 10, paddingRight: 10,
              border: '1px solid #FFEFE6', borderRadius: 10,
              backgroundColor: '#fff', display: 'flex', alignItems: 'center',
              opacity: !startDate ? 0.45 : 1,
              transition: 'opacity 0.2s',
            }}>
              <input
                type="date"
                value={endDate}
                min={startDate}
                disabled={!startDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%', height: '100%', border: 'none', outline: 'none',
                  backgroundColor: 'transparent', fontSize: 16,
                  color: endDate ? theme.colors.mainDark : '#9CA3AF',
                  fontFamily: 'inherit',
                  cursor: !startDate ? 'not-allowed' : 'pointer',
                }}
              />
            </div>
        </div>

        <text.H5 style={{marginBottom: 10}}>{t.leaveRequest.reason}</text.H5>
        <div style={{marginBottom: 20, padding: 14, border: '1px solid #FFEFE6', borderRadius: 10, backgroundColor: '#fff'}}>
            <textarea 
                placeholder={t.leaveRequest.reasonPlaceholder}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{width: '100%', height: 100, border: 'none', outline: 'none', fontSize: 16, fontFamily: 'SourceSansPro_400Regular', resize: 'none'}}
            />
        </div>

        <text.H5 style={{marginBottom: 10}}>{t.leaveRequest.attachment}</text.H5>
        <div style={{marginBottom: 30}}>
          {!attachment ? (
            <div 
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '1px dashed #9CA3AF', borderRadius: 10, padding: 20,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#F9FAFB', cursor: 'pointer'
              }}
            >
              <AttachFileIcon size={32} color="#9CA3AF" />
              <text.T14 style={{marginTop: 8, color: '#6B7280'}}>{t.leaveRequest.tapToUpload}</text.T14>
            </div>
          ) : (
            <div>
              {attachment.type.startsWith('image/') ? (
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                  <img src={URL.createObjectURL(attachment)} alt="Preview" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div onClick={() => setAttachment(null)} style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', padding: 6, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%' }}>
                    <DeleteIcon size={20} color="#EF4444" />
                  </div>
                </div>
              ) : (
                <div style={{
                  border: '1px solid #E5E7EB', borderRadius: 10, padding: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  backgroundColor: '#F9FAFB'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden'}}>
                    <AttachFileIcon size={24} color={theme.colors.mainColor} />
                    <text.T14 style={{color: theme.colors.mainDark, fontWeight: '500'}} numberOfLines={1}>
                      {attachment.name}
                    </text.T14>
                  </div>
                  <div onClick={() => setAttachment(null)} style={{cursor: 'pointer', padding: 4}}>
                    <DeleteIcon size={24} color="#EF4444" />
                  </div>
                </div>
              )}
            </div>
          )}
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            style={{display: 'none'}} 
            accept="image/*,.pdf,.doc,.docx"
          />
        </div>

        <components.Button title={isLoading ? t.leaveRequest.submitting : t.leaveRequest.submit} onClick={handleSubmit} />
      </main>
    );
  };

  return (
    <div id='screen' style={{backgroundColor: '#fff', minHeight: '100vh'}}>
        {renderHeader()}
        {renderContent()}
        {renderAlert()}
    </div>
  );
};